import type { InterestScore } from '../../types';
import './ScoreBreakdown.css';

interface ScoreBreakdownProps {
  interestScore: InterestScore;
}

export function ScoreBreakdown({ interestScore }: ScoreBreakdownProps) {
  // Guard against undefined interestScore
  if (!interestScore || typeof interestScore !== 'object') {
    return (
      <div className="score-breakdown">
        <h4 className="score-breakdown__title">Interest Score Breakdown</h4>
        <p className="text-muted">Score data not available</p>
      </div>
    );
  }

  const { total = 0, components = {} } = interestScore;
  const {
    sourceWeight = 0,
    frequency = 0,
    recency = 0,
    titleSignals = 0
  } = components as {
    sourceWeight?: number; 
    frequency?: number; 
    recency?: number; 
    titleSignals?: number; 
  };

  const scoreItems = [
    { 
      label: 'Source Authority', 
      value: sourceWeight, 
      description: 'Credibility and expertise of the source'
    },
    { 
      label: 'Cross-Source Frequency', 
      value: frequency, 
      description: 'How many sources covered this story'
    },
    { 
      label: 'Recency', 
      value: recency, 
      description: 'How recently the story was published'
    },
    { 
      label: 'Title Signals', 
      value: titleSignals, 
      description: 'Keywords indicating importance'
    }
  ];

  const getScoreColor = (value: number) => {
    if (value >= 0.8) return 'var(--color-success)';
    if (value >= 0.6) return 'var(--color-accent)';
    if (value >= 0.4) return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  return (
    <div className="score-breakdown">
      <h4 className="score-breakdown__title">Interest Score Breakdown</h4>
      
      <div className="score-breakdown__items">
        {scoreItems.map((item) => (
          <div key={item.label} className="score-breakdown__item">
            <div className="score-breakdown__item-header">
              <span className="score-breakdown__label">{item.label}:</span>
              <span 
                className="score-breakdown__value"
                style={{ color: getScoreColor(item.value) }}
              >
                {item.value.toFixed(2)}
              </span>
            </div>
            
            <div className="score-breakdown__bar">
              <div 
                className="score-breakdown__fill"
                style={{ 
                  width: `${item.value * 100}%`,
                  backgroundColor: getScoreColor(item.value)
                }}
              />
            </div>
            
            <p className="score-breakdown__description">{item.description}</p>
          </div>
        ))}
      </div>

      <div className="score-breakdown__total">
        <div className="score-breakdown__total-row">
          <span className="score-breakdown__total-label">Total Score:</span>
          <span 
            className="score-breakdown__total-value"
            style={{ color: getScoreColor(total) }}
          >
            {total.toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
}